import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, Building2, MapPin, MessageSquare, RefreshCw, Loader2 } from 'lucide-react';
import { adminApi } from '@/services/api';
import { toast } from 'sonner';

const AdminQuoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [resending, setResending] = useState(false);

  const statusOptions = [
    { value: 'nueva', label: 'Nueva', color: 'bg-blue-100 text-blue-800' },
    { value: 'contactada', label: 'Contactada', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'cotizada', label: 'Cotizada', color: 'bg-green-100 text-green-800' },
    { value: 'cerrada', label: 'Cerrada', color: 'bg-gray-100 text-gray-800' },
  ];

  useEffect(() => {
    loadQuote();
  }, [id]);

  const loadQuote = async () => {
    try {
      const response = await adminApi.getQuote(id);
      setQuote(response.data);
    } catch (error) {
      toast.error('Error al cargar la cotización');
      navigate('/admin/cotizaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      await adminApi.updateQuoteStatus(id, newStatus);
      setQuote({ ...quote, estado: newStatus });
      toast.success('Estado actualizado');
    } catch (error) {
      toast.error('Error al actualizar el estado');
    } finally {
      setUpdating(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await adminApi.resendQuote(id);
      toast.success('Correo reenviado');
    } catch (error) {
      toast.error('Error al reenviar el correo');
    } finally {
      setResending(false);
    }
  };

  const getWhatsAppLink = () => {
    if (!quote) return '#';
    const products = quote.productos?.map(p => `• ${p.codigo ? `[${p.codigo}] ` : ''}${p.nombre}${p.variacion ? ` - ${p.variacion}` : ''} (${p.cantidad}) ${p.notas ? `- ${p.notas}` : ''}`).join('\n') || '';
    const message = `Hola ${quote.cliente_nombre}, gracias por tu cotización ${quote.folio}.\n\nProductos solicitados:\n${products}\n\nNos pondremos en contacto contigo pronto.`;
    return `https://wa.me/52${quote.cliente_telefono.replace(/\s/g, '')}?text=${encodeURIComponent(message)}`;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 size={40} className="animate-spin text-accent-primary" />
      </div>
    );
  }

  if (!quote) return null;

  const currentStatus = statusOptions.find(s => s.value === quote.estado) || statusOptions[0];

  return (
    <div className="p-6">
      <Link to="/admin/cotizaciones" className="inline-flex items-center gap-2 text-steel-gray hover:text-accent-primary mb-6">
        <ArrowLeft size={18} />
        Volver a cotizaciones
      </Link>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Main Content */}
        <div className="flex-1">
          {/* Header */}
          <div className="bg-white rounded shadow p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="font-heading text-2xl font-bold text-steel-dark">
                  Cotización {quote.folio}
                </h1>
                <p className="text-steel-gray text-sm mt-1">
                  {new Date(quote.created_at).toLocaleString('es-MX')}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={quote.estado}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={updating}
                  className={`px-4 py-2 rounded font-medium ${currentStatus.color} border-0 cursor-pointer`}
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Products */}
          <div className="bg-white rounded shadow overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h2 className="font-heading font-bold text-lg text-steel-dark uppercase">
                Productos Solicitados ({quote.productos?.length || 0})
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Código</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Producto / Variación</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Cantidad</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Notas</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {quote.productos?.map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-steel-gray">{idx + 1}</td>
                      <td className="px-4 py-3">
                        {item.codigo ? (
                          <span className="font-mono text-accent-primary font-bold bg-accent-primary/10 px-2 py-1 rounded text-xs">{item.codigo}</span>
                        ) : (
                          <span className="text-sm text-steel-gray">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-steel-dark">{item.nombre}</p>
                        {item.variacion && <p className="text-xs text-steel-gray mt-0.5">{item.variacion}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="bg-steel-dark text-white px-3 py-1 rounded text-sm">
                          {item.cantidad}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-steel-gray">{item.notas || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Comments */}
          {quote.comentarios && (
            <div className="bg-white rounded shadow p-6 mt-6">
              <h2 className="font-heading font-bold text-lg text-steel-dark uppercase mb-3">
                Comentarios del Cliente
              </h2>
              <p className="text-steel-gray bg-gray-50 p-4 rounded">{quote.comentarios}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:w-80">
          {/* Client Info */}
          <div className="bg-white rounded shadow p-6 mb-6">
            <h2 className="font-heading font-bold text-lg text-steel-dark uppercase mb-4">
              Datos del Cliente
            </h2>
            
            <div className="space-y-4">
              <div>
                <p className="font-semibold text-steel-dark text-lg">{quote.cliente_nombre}</p>
              </div>
              
              {quote.cliente_empresa && (
                <div className="flex items-start gap-3">
                  <Building2 size={18} className="text-accent-primary mt-0.5" />
                  <span className="text-steel-gray">{quote.cliente_empresa}</span>
                </div>
              )}
              
              <div className="flex items-start gap-3">
                <Mail size={18} className="text-accent-primary mt-0.5" />
                <a href={`mailto:${quote.cliente_email}`} className="text-accent-primary hover:text-accent-hover">
                  {quote.cliente_email}
                </a>
              </div>
              
              <div className="flex items-start gap-3">
                <Phone size={18} className="text-accent-primary mt-0.5" />
                <a href={`tel:${quote.cliente_telefono}`} className="text-accent-primary hover:text-accent-hover">
                  {quote.cliente_telefono}
                </a>
              </div>
              
              {quote.cliente_ciudad && (
                <div className="flex items-start gap-3">
                  <MapPin size={18} className="text-accent-primary mt-0.5" />
                  <span className="text-steel-gray">{quote.cliente_ciudad}</span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded shadow p-6">
            <h2 className="font-heading font-bold text-lg text-steel-dark uppercase mb-4">
              Acciones
            </h2>
            
            <div className="space-y-3">
              <a
                href={getWhatsAppLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                <MessageSquare size={18} />
                Contactar por WhatsApp
              </a>
              
              <button
                onClick={handleResend}
                disabled={resending}
                className="btn-outline w-full flex items-center justify-center gap-2"
              >
                {resending ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
                Reenviar Correo
              </button>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-steel-gray">
              <p className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${quote.notificacion_email ? 'bg-green-500' : 'bg-red-500'}`} />
                Email {quote.notificacion_email ? 'enviado' : 'no enviado'}
              </p>
              <p className="flex items-center gap-2 mt-1">
                <span className={`w-2 h-2 rounded-full ${quote.notificacion_whatsapp ? 'bg-green-500' : 'bg-red-500'}`} />
                WhatsApp {quote.notificacion_whatsapp ? 'enviado' : 'no enviado'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminQuoteDetail;
